import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  size,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
  useRole,
  useTypeahead,
} from '@floating-ui/react';
import { faAngleDown, faAngleUp } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useRef, useState } from 'react';

interface SelectProps<T> {
  extractKey: (item: T) => string;
  options: T[];
  placeholder?: string;
  onChange?: (option: T) => void;
  // since option is generic (unknown) object to decide what would be used as value/label
  renderValue: (item: T) => string;
  renderLabel: (item: T) => string;
  // if custom option render is neccessary
  renderOptionItem?: (item: T) => React.ReactNode;
  value?: T;
  width?: number | string;
  selectedBorderColor?: string;
  selectedBorderColorHover?: string;
  selectedColor?: string;
}

const Select = <T,>({
  extractKey,
  options,
  onChange,
  placeholder = 'select...',
  renderValue,
  renderLabel,
  renderOptionItem,
  width = 120,
  value,
  selectedBorderColor,
  selectedBorderColorHover,
  selectedColor,
}: SelectProps<T>) => {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const { x, y, strategy, refs, context } = useFloating({
    placement: 'bottom-start',
    open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(5),
      flip({ padding: 10 }),
      size({
        apply({ rects, elements, availableHeight }) {
          Object.assign(elements.floating.style, {
            maxHeight: `${availableHeight}px`,
            width: `${rects.reference.width}px`,
          });
        },
        padding: 10,
      }),
    ],
  });

  const listRef = useRef<Array<HTMLElement | null>>([]);
  const listContentRef = useRef(options.map((item) => renderLabel(item).toLowerCase()));

  useEffect(() => {
    if (options.length > 0) {
      listContentRef.current = options.map((item) => renderLabel(item).toLowerCase());
    }
  }, [options, renderLabel]);

  useEffect(() => {
    if (value) {
      const index = options.findIndex((item) => {
        return renderValue(item) === renderValue(value);
      });
      setSelectedIndex(index);
    }
  }, [options, value, renderValue]);

  const click = useClick(context, { event: 'mousedown' });
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'listbox' });
  const listNav = useListNavigation(context, {
    listRef,
    activeIndex,
    selectedIndex,
    onNavigate: setActiveIndex,
    // This is a large list, allow looping.
    loop: true,
  });
  const typeahead = useTypeahead(context, {
    listRef: listContentRef,
    activeIndex,
    selectedIndex,
    onMatch: open ? setActiveIndex : setSelectedIndex,
  });

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([
    click,
    dismiss,
    role,
    listNav,
    typeahead,
  ]);

  const handleSelect = (index: number) => {
    setSelectedIndex(index);
    setOpen(false);
  };

  const selectedItemLabel =
    selectedIndex !== null && options[selectedIndex]
      ? renderLabel(options[selectedIndex])
      : undefined;

  return (
    <>
      <div
        className={`duration-150xl:text-base inline-flex cursor-pointer items-center whitespace-nowrap rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm text-gray-900 transition ${
          open
            ? selectedBorderColor
              ? selectedBorderColor
              : 'border-blue-300 ring ring-blue-50'
            : selectedBorderColorHover
              ? selectedBorderColorHover
              : 'hover:border-blue-300 hover:ring hover:ring-blue-50'
        }`}
        ref={refs.setReference}
        style={{ width }}
        aria-label={selectedItemLabel}
        aria-autocomplete='none'
        {...getReferenceProps()}
        // The default role for the reference using a "listbox"
        // is a "combobox", but Safari has a bug with VoiceOver
        // where it cuts off letters when announcing the button's
        // content when it has that role.
        // This overrides the one from the props above.
        role={undefined}
      >
        <div className='flex-1 overflow-hidden text-ellipsis'>
          {selectedItemLabel || placeholder}
        </div>
        {open ? (
          <FontAwesomeIcon
            icon={faAngleUp}
            className='ml-auto text-gray-500'
            title='Close dropdown'
          />
        ) : (
          <FontAwesomeIcon
            icon={faAngleDown}
            className='ml-auto text-gray-500'
            title='Open dropdown'
          />
        )}
      </div>

      <FloatingPortal>
        {open ? (
          <FloatingFocusManager context={context} modal={false}>
            <div
              ref={refs.setFloating}
              className='shadow relative z-50 flex max-h-60 flex-col space-y-1 overflow-y-auto rounded border-none bg-white p-2 outline-none'
              style={{
                position: strategy,
                top: y ?? 0,
                left: x ?? 0,
                overflowY: 'auto',
                padding: 0,
                outline: 0,
              }}
              {...getFloatingProps()}
            >
              <div style={{ maxHeight: 220, overflowY: 'auto', padding: 10 }}>
                {options.map((option, i) => (
                  <div
                    key={extractKey(option)}
                    ref={(node) => {
                      listRef.current[i] = node;
                    }}
                    role='option'
                    tabIndex={i === activeIndex ? 0 : -1}
                    aria-selected={i === selectedIndex && i === activeIndex}
                    className={`block w-full cursor-pointer rounded px-3 py-1.5 text-left text-sm outline-none transition duration-150 xl:text-base ${
                      i === selectedIndex
                        ? selectedColor
                          ? selectedColor
                          : 'bg-blue-500 text-white hover:bg-blue-500'
                        : 'hover:bg-gray-200 focus:bg-gray-100'
                    }`}
                    {...getItemProps({
                      // Handle pointer select.
                      onClick() {
                        handleSelect(i);
                        if (onChange) onChange(option);
                      },
                      // Handle keyboard select.
                      onKeyDown(event) {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          handleSelect(i);
                        }

                        // Only if not using typeahead.
                        if (event.key === ' ' && !context.dataRef.current.typing) {
                          event.preventDefault();
                          handleSelect(i);
                        }
                      },
                    })}
                  >
                    {renderOptionItem ? renderOptionItem(option) : renderLabel(option)}
                  </div>
                ))}
              </div>
            </div>
          </FloatingFocusManager>
        ) : null}
      </FloatingPortal>
    </>
  );
};

export default Select;
